import type { LlmEnrichmentResult } from '../../lib/llm';

interface Props {
  /** settings.llmEnabled */
  enabled: boolean;
  /** This device can't run the on-device model (graceful, silent). */
  deviceUnsupported: boolean;
  /** engine state === 'ready' */
  ready: boolean;
  state: 'not_run' | 'running' | 'done' | 'failed';
  result: LlmEnrichmentResult | null;
  /** Live LLM-call progress while `state === 'running'`. */
  progress?: { done: number; total: number } | null;
}

const BASE = 'rounded-lg border p-3 text-xs';

/** Errors that mean "this device just can't run the model", not a real bug. */
function looksLikeCapabilityErrors(errs: string[]): boolean {
  if (errs.length === 0) return false;
  return errs.every((e) =>
    /not ready|state=error|gpu|memory|device|disposed|webgpu|resource-intensive/i.test(e),
  );
}

/**
 * Always tells the editor what the LLM did — even when it produced nothing.
 * Without this the only signal was an ephemeral "· LLM enriching…" string and
 * a silent disappearance, so a failed or empty enrichment looked identical to
 * pattern-only mode. On `done` it also surfaces per-call errors, which is the
 * fastest way to see *why* grades / the rationale draft are missing.
 */
export function EnrichmentStatusCard({
  enabled,
  deviceUnsupported,
  ready,
  state,
  result,
  progress,
}: Props) {
  if (!enabled) return null;
  // The LlmStatusCard already shows one calm line about this device; don't
  // also dump a scary enrichment-failure card.
  if (deviceUnsupported) return null;

  const CALM = `${BASE} border-slate-200 bg-slate-50 text-slate-600`;

  if (!ready && state === 'not_run') {
    return (
      <div className={`${BASE} border-amber-200 bg-amber-50 text-amber-800`}>
        <p className="font-semibold">LLM enabled but not loaded yet</p>
        <p className="mt-0.5">
          Load the model in the panel above. Enrichment runs automatically when you open the
          next manuscript.
        </p>
      </div>
    );
  }

  if (state === 'running') {
    const total = progress && progress.total > 0 ? progress.total : 0;
    const done = progress ? Math.min(progress.done, total || progress.done) : 0;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
      <div className={`${BASE} border-sky-200 bg-sky-50 text-sky-800`}>
        <div className="flex items-baseline justify-between gap-2">
          <p className="font-semibold">LLM enriching…</p>
          {total > 0 && (
            <span className="text-[11px] tabular-nums text-sky-700">
              {done}/{total} calls
            </span>
          )}
        </div>
        <div
          className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-sky-200"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={total || undefined}
          aria-valuenow={total > 0 ? done : undefined}
          aria-label="LLM enrichment progress"
        >
          {total > 0 ? (
            <div
              className="h-full rounded-full bg-sky-500 transition-[width] duration-500 ease-out"
              style={{ width: `${Math.max(4, pct)}%` }}
            />
          ) : (
            // No estimate yet — brief indeterminate sweep.
            <div className="h-full w-1/3 animate-pulse rounded-full bg-sky-500" />
          )}
        </div>
        <p className="mt-1.5">
          Grading checklist items and assessing spin. Each call is paced (cloud mode respects
          the free-tier rate limit), so this can take a few minutes — keep this tab open;
          results fill in as it progresses.
        </p>
      </div>
    );
  }

  if (state === 'not_run') {
    return (
      <div className={`${BASE} border-slate-200 bg-slate-50 text-slate-600`}>
        <p className="font-semibold">LLM loaded — not run for this manuscript</p>
        <p className="mt-0.5">
          The model finished loading after this manuscript opened. Click “+ New manuscript” and
          re-open it (or re-upload) to enrich it.
        </p>
      </div>
    );
  }

  if (state === 'failed') {
    if (looksLikeCapabilityErrors(result?.errors ?? [])) {
      return (
        <div className={CALM}>
          On-device AI isn’t available on this device — showing pattern-engine results only
          (this is expected on many laptops; the AI is optional).
        </div>
      );
    }
    return (
      <div className={`${BASE} border-red-200 bg-red-50 text-red-800`}>
        <p className="font-semibold">LLM enrichment failed</p>
        <p className="mt-0.5 break-words">
          {result?.errors?.length
            ? result.errors.join(' · ')
            : 'The engine errored or was not ready. Open DevTools → Console for details.'}
        </p>
      </div>
    );
  }

  // state === 'done'
  const graded = result?.itemGrades.length ?? 0;
  const reclassified = Boolean(result?.classifier);
  const spin = (result?.extraFindings ?? []).some((f) => f.findingText.startsWith('Spin'));
  const errs = result?.errors ?? [];
  const allFailed = graded === 0 && errs.length > 0;

  if (allFailed && looksLikeCapabilityErrors(errs)) {
    return (
      <div className={CALM}>
        On-device AI isn’t available on this device — showing pattern-engine results only
        (this is expected on many laptops; the AI is optional).
      </div>
    );
  }

  const tone = allFailed
    ? 'border-red-200 bg-red-50 text-red-800'
    : errs.length
      ? 'border-amber-200 bg-amber-50 text-amber-800'
      : 'border-emerald-200 bg-emerald-50 text-emerald-800';

  return (
    <div className={`${BASE} ${tone}`}>
      <p className="font-semibold">
        {allFailed ? 'LLM ran but produced no usable output' : 'LLM enrichment complete'}
      </p>
      <p className="mt-0.5">
        {graded} checklist item{graded === 1 ? '' : 's'} graded
        {reclassified ? ' · study type re-checked' : ''}
        {spin ? ' · spin assessed' : ''}
        {result ? ` · ${result.modelId}` : ''}
      </p>
      {errs.length > 0 && (
        <div className="mt-1.5 border-t border-black/10 pt-1.5">
          <p className="font-semibold">
            {errs.length} call{errs.length === 1 ? '' : 's'} failed:
          </p>
          <ul className="mt-0.5 list-disc space-y-0.5 break-words pl-4">
            {errs.slice(0, 6).map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
          {allFailed && (
            <p className="mt-1">
              The model responded but its output did not match the expected JSON schema (often the
              model wraps JSON in prose/markdown). The pattern-engine results above are unaffected.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
